import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { convertTeamNameToJerseyFormat, type Sport } from '@/utils/jersey-mapping';

type ExtendedSport = Sport | 'PROPS' | 'NBA' | 'NCAA' | 'MLB';

const CDN_BASE_URL = 'https://bet99pickemdev.blob.core.windows.net';

const NHL_TEAMS = [
  'Anaheim_Ducks',
  'Boston_Bruins',
  'Buffalo_Sabres',
  'Calgary_Flames',
  'Carolina_Hurricanes',
  'Chicago_Blackhawks',
  'Colorado_Avalanche',
  'Columbus_Blue_Jackets',
  'Dallas_Stars',
  'Detroit_Red_Wings',
  'Edmonton_Oilers',
  'Florida_Panthers',
  'Los_Angeles_Kings',
  'Minnesota_Wild',
  'Montreal_Canadiens',
  'Nashville_Predators',
  'New_Jersey_Devils',
  'New_York_Islanders',
  'New_York_Rangers',
  'Ottawa_Senators',
  'Philadelphia_Flyers',
  'Pittsburgh_Penguins',
  'San_Jose_Sharks',
  'Seattle_Kraken',
  'St_Louis_Blues',
  'Tampa_Bay_Lightning',
  'Toronto_Maple_Leafs',
  'Utah_Hockey_Club',
  'Vancouver_Canucks',
  'Vegas_Golden_Knights',
  'Washington_Capitals',
  'Winnipeg_Jets',
];

const NFL_TEAMS = [
  'Arizona Cardinals',
  'Atlanta Falcons',
  'Baltimore Ravens',
  'Buffalo Bills',
  'Carolina Panthers',
  'Chicago Bears',
  'Cincinnati Bengals',
  'Cleveland Browns',
  'Dallas Cowboys',
  'Denver Broncos',
  'Detroit Lions',
  'Green Bay Packers',
  'Houston Texans',
  'Indianapolis Colts',
  'Jacksonville Jaguars',
  'Kansas City Chiefs',
  'Las Vegas Raiders',
  'Los Angeles Chargers',
  'Los Angeles Rams',
  'Miami Dolphins',
  'Minnesota Vikings',
  'New England Patriots',
  'New Orleans Saints',
  'New York Giants',
  'New York Jets',
  'Philadelphia Eagles',
  'Pittsburgh Steelers',
  'San Francisco 49ers',
  'Seattle Seahawks',
  'Tampa Bay Buccaneers',
  'Tennessee Titans',
  'Washington Commanders',
];

const MLB_TEAMS = [
  'Arizona Diamondbacks',
  'Atlanta Braves',
  'Baltimore Orioles',
  'Boston Red Sox',
  'Chicago Cubs',
  'Chicago White Sox',
  'Cincinnati Reds',
  'Cleveland Guardians',
  'Colorado Rockies',
  'Detroit Tigers',
  'Houston Astros',
  'Kansas City Royals',
  'Los Angeles Angels',
  'Los Angeles Dodgers',
  'Miami Marlins',
  'Milwaukee Brewers',
  'Minnesota Twins',
  'New York Mets',
  'New York Yankees',
  'Oakland Athletics',
  'Philadelphia Phillies',
  'Pittsburgh Pirates',
  'San Diego Padres',
  'San Francisco Giants',
  'Seattle Mariners',
  'St. Louis Cardinals',
  'Tampa Bay Rays',
  'Texas Rangers',
  'Toronto Blue Jays',
  'Washington Nationals',
];

const PROP_OPTIONS = ['Seattle Seahawks', 'New England Patriots', 'Gatorade', 'Anthem'];

const NBA_TEAMS = [
  'Memphis Grizzlies',
  'LA Clippers',
  'Orlando Magic',
  'Oklahoma City Thunder',
  'Toronto Raptors',
  'Utah Jazz',
  'Houston Rockets',
  'Washington Wizards',
  'Miami Heat',
  'Cleveland Cavaliers',
  'Atlanta Hawks',
  'Indiana Pacers',
  'San Antonio Spurs',
  'Denver Nuggets',
  'LA Lakers',
  'Phoenix Suns',
  'New Orleans Pelicans',
  'New York Knicks',
  'Minnesota Timberwolves',
  'Detroit Pistons',
  'Philidelphia 76ers',
  'Boston Celtics',
  'Sacramento Kings',
  'Golden State Warriors',
  'Portland Trail Blazers',
  'Chicago Bulls',
  'Charlotte Hornets',
  'Milwaukee Bucks',
  'Dallas Mavericks',
];

const NCAA_TEAMS = [
  'Akron',
  'Alabama',
  'Arizona',
  'Auburn',
  'BYU',
  'Baylor',
  'Boise State',
  'Charleston',
  'Clemson',
  'Colgate',
  'Colorado',
  'Colorado State',
  'Connecticut',
  'Creighton',
  'Dayton',
  'Drake',
  'Duke',
  'Florida',
  'Florida Atlantic',
  'Gonzaga',
  'Grambling State',
  'Houston Cougars',
  'Howard',
  'ISU',
  'Illinois',
  'James Madison',
  'Kansas',
  'Kentucky',
  'Long Beach State',
  'Longwood',
  'Marquette',
  'McNeese State',
  'Michigan State',
  'Mississippi State',
  'Montana State',
  'Morehead State',
  'NC State',
  'Nebraska',
  'Nevada',
  'New Mexico',
  'North Carolina',
  'Northwestern',
  'Oakland',
  'Oklahoma',
  'Oregon',
  'Purdue',
  'Saint Marys',
  'Saint Peters',
  'Samford',
  'San Diego',
  'Seton Hall',
  'South Carolina',
  'South Dakota State',
  'Stetson',
  'TCU',
  'Tennessee',
  'Texas',
  'Texas AM',
  'Texas Tech',
  'Utah State',
  'Vermont',
  'Virginia',
  'Wagner',
  'Washington State',
  'Western Kentucky',
  'Wisconsin',
];

const JERSEY_NUMBERS = Array.from({ length: 100 }, (_, i) => i.toString());

interface JerseySelectorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (url: string) => void;
  fieldLabel?: string;
}

export function JerseySelectorModal({
  open,
  onOpenChange,
  onSelect,
  fieldLabel = 'Image',
}: JerseySelectorModalProps) {
  const [selectedSport, setSelectedSport] = useState<ExtendedSport>('NHL');
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [selectedProp, setSelectedProp] = useState<string>('');
  const [selectedNumber, setSelectedNumber] = useState<string>('blank');
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [imageError, setImageError] = useState(false);

  // Get teams for selected sport
  // const teams = useMemo(() => {
  //   return getAllTeams(selectedSport);
  // }, [selectedSport]);

  // Reset team selection when sport changes
  useEffect(() => {
    setSelectedTeam(selectedTeam);
  }, [selectedSport]);

  useEffect(() => {
    let url: string;

    if (selectedSport === 'PROPS') {
      const propName = selectedProp.replace(/\s+/g, '_').toLowerCase();

      url = `${CDN_BASE_URL}/props/${propName}_prop.svg`;

      setPreviewUrl(url);
      setImageError(false);
    } else if (selectedTeam) {
      const teamName = convertTeamNameToJerseyFormat(selectedTeam);

      let url = `${CDN_BASE_URL}/jerseys/${teamName}`;

      if (selectedNumber && selectedNumber !== 'blank') {
        url += `_${selectedNumber}`;
      }
      url += '.png';

      setPreviewUrl(url);
      setImageError(false);
    } else {
      setPreviewUrl('');
    }
  }, [selectedTeam, selectedNumber, selectedSport, selectedProp]);

  const handleSelect = () => {
    if (previewUrl) {
      onSelect(previewUrl);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedSport('NHL');
    setSelectedTeam('');
    setSelectedNumber('blank');
    setSelectedProp('');
    setPreviewUrl('');
    setImageError(false);
    onOpenChange(false);
  };

  const formatTeamName = (teamKey: string) => {
    return teamKey.replace(/_/g, ' ');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Select Jersey {fieldLabel && `for ${fieldLabel}`}</DialogTitle>
          <DialogDescription>Choose a sport, team, and optional jersey number</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="sport-select">Sport</Label>
            <Select
              value={selectedSport}
              onValueChange={(value: ExtendedSport) => setSelectedSport(value)}
            >
              <SelectTrigger id="sport-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NHL">NHL (Hockey)</SelectItem>
                <SelectItem value="NFL">NFL (Football)</SelectItem>
                <SelectItem value="PROPS">Props</SelectItem>
                <SelectItem value="NBA">NBA</SelectItem>
                <SelectItem value="NCAA">NCAA</SelectItem>
                <SelectItem value="MLB">MLB</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {(selectedSport === 'NFL' ||
            selectedSport === 'NHL' ||
            selectedSport === 'NBA' ||
            selectedSport === 'MLB' ||
            selectedSport === 'NCAA') && (
            <div className="space-y-2">
              <Label htmlFor="team-select">Team</Label>
              <Select value={selectedTeam} onValueChange={setSelectedTeam}>
                <SelectTrigger id="team-select">
                  <SelectValue placeholder="Select a team" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {selectedSport === 'NHL' &&
                    NHL_TEAMS.map(team => (
                      <SelectItem key={team} value={team}>
                        {formatTeamName(team)}
                      </SelectItem>
                    ))}
                  {selectedSport === 'NFL' &&
                    NFL_TEAMS.map(team => (
                      <SelectItem key={team} value={team}>
                        {formatTeamName(team)}
                      </SelectItem>
                    ))}
                  {selectedSport === 'NBA' &&
                    NBA_TEAMS.map(team => (
                      <SelectItem key={team} value={team}>
                        {formatTeamName(team)}
                      </SelectItem>
                    ))}
                  {selectedSport === 'NCAA' &&
                    NCAA_TEAMS.map(team => (
                      <SelectItem key={team} value={team}>
                        {formatTeamName(team)}
                      </SelectItem>
                    ))}
                  {selectedSport === 'MLB' &&
                    MLB_TEAMS.map(team => (
                      <SelectItem key={team} value={team}>
                        {formatTeamName(team)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {selectedSport === 'PROPS' && (
            <div className="space-y-2">
              <Label htmlFor="prop-select">Props</Label>
              <Select value={selectedProp} onValueChange={setSelectedProp}>
                <SelectTrigger id="prop-select">
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {PROP_OPTIONS.map(prop => (
                    <SelectItem key={prop} value={prop}>
                      {formatTeamName(prop)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {selectedTeam && (
            <div className="space-y-2">
              <Label htmlFor="number-select">Jersey Number (Optional)</Label>
              <Select value={selectedNumber} onValueChange={setSelectedNumber}>
                <SelectTrigger id="number-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="blank">Blank (No Number)</SelectItem>
                  {JERSEY_NUMBERS.map(num => (
                    <SelectItem key={num} value={num}>
                      #{num}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* {selectedTeam && selectedSport === 'NFL' && (
            <p className="text-sm text-muted-foreground">
              NFL teams use a single jersey design for all players
            </p>
          )} */}

          {previewUrl && (
            <div className="space-y-2">
              <Label>Preview</Label>
              <div className="relative w-full h-64 border rounded-md overflow-hidden bg-gray-50">
                {!imageError ? (
                  <img
                    src={previewUrl}
                    alt="Jersey preview"
                    className="w-full h-full object-contain"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="flex w-full h-full items-center justify-center text-sm text-muted-foreground">
                    Failed to load image
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground break-all">{previewUrl}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSelect} disabled={!previewUrl || imageError}>
            Select
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
